// NOTICE: KAPTION-INTEGRATION
// usage-analytics.TRANSPORT.1 — Best-effort batching transport for product-usage
// telemetry. Everything here is wrapped so it NEVER throws into product code:
// telemetry is not mission-critical and must not be able to break the dashboard.
//
// Batching/flush triggers (shared contract with the worker):
//   - queue size >= FLUSH_BATCH_SIZE
//   - every FLUSH_INTERVAL_MS (timer)
//   - route change (composable calls flush())
//   - document visibilitychange -> hidden, and pagehide
//
// Delivery: POST {workerUrl}{EVENTS_PATH}, body = JSON ARRAY of envelopes,
// header Authorization: Bearer <jwt>. Uses fetch(keepalive) and falls back to
// navigator.sendBeacon (for the unload path / when fetch is unavailable).
import { FLUSH_BATCH_SIZE, FLUSH_INTERVAL_MS, EVENTS_PATH } from './constants';

export class UsageAnalyticsTransport {
  constructor() {
    this.queue = [];
    this.timer = null;
    // Async provider of { token, workerUrl } | null. Injected by the composable
    // so the transport doesn't import the Vuex store directly. The provider is
    // responsible for minting/refreshing the token.
    this.tokenProvider = null;
    // Called with no args when the worker responds 401, so the store can drop
    // its cached token. Injected by the composable.
    this.onUnauthorized = null;
    this.listenersBound = false;
  }

  // usage-analytics.TRANSPORT.2 — wire the transport to a token provider +
  // unauthorized handler and start the flush timer + unload listeners. Idempotent.
  configure({ tokenProvider, onUnauthorized } = {}) {
    this.tokenProvider = tokenProvider || this.tokenProvider;
    this.onUnauthorized = onUnauthorized || this.onUnauthorized;
    this.startTimer();
    this.bindUnloadListeners();
  }

  enqueue(event) {
    try {
      if (!event) return;
      this.queue.push(event);
      if (this.queue.length >= FLUSH_BATCH_SIZE) {
        this.flush();
      }
    } catch (e) {
      // swallow — never break product code
    }
  }

  startTimer() {
    try {
      if (this.timer || typeof setInterval === 'undefined') return;
      this.timer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
      // Don't keep a Node/test process alive on account of the timer.
      if (this.timer && typeof this.timer.unref === 'function') {
        this.timer.unref();
      }
    } catch (e) {
      // swallow
    }
  }

  stopTimer() {
    try {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    } catch (e) {
      // swallow
    }
  }

  bindUnloadListeners() {
    try {
      if (this.listenersBound || typeof document === 'undefined') return;
      // visibilitychange -> hidden is the reliable "user is leaving" signal on
      // mobile; pagehide covers bfcache/unload. Both use the beacon path.
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush({ useBeacon: true });
        }
      });
      if (typeof window !== 'undefined') {
        window.addEventListener('pagehide', () =>
          this.flush({ useBeacon: true })
        );
      }
      this.listenersBound = true;
    } catch (e) {
      // swallow
    }
  }

  // usage-analytics.TRANSPORT.3 — drain the queue. Best-effort and async, but
  // never rejects. `useBeacon` forces the sendBeacon path for the unload case
  // (where async fetch may be killed).
  async flush({ useBeacon = false } = {}) {
    let batch;
    try {
      if (this.queue.length === 0) return;
      batch = this.queue;
      this.queue = [];

      const creds = this.tokenProvider ? await this.tokenProvider() : null;
      if (!creds || !creds.token || !creds.workerUrl) {
        // No usable token (feature off / mint failed). Drop the batch silently;
        // re-queueing risks unbounded growth and this is best-effort.
        return;
      }

      const url = `${creds.workerUrl}${EVENTS_PATH}`;
      const body = JSON.stringify(batch);

      if (useBeacon) {
        this.sendViaBeacon(url, body, creds.token);
        return;
      }

      await this.sendViaFetch(url, body, creds.token);
    } catch (e) {
      // swallow — telemetry must never throw
    }
  }

  async sendViaFetch(url, body, token) {
    try {
      if (typeof fetch === 'undefined') {
        this.sendViaBeacon(url, body, token);
        return;
      }
      const res = await fetch(url, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body,
      });
      // usage-analytics.TOKEN.3 — let the store drop the token on 401 so the
      // next flush re-mints.
      if (
        res &&
        res.status === 401 &&
        typeof this.onUnauthorized === 'function'
      ) {
        this.onUnauthorized();
      }
    } catch (e) {
      // Network error: fall back to beacon best-effort.
      this.sendViaBeacon(url, body, token);
    }
  }

  // sendBeacon can't set Authorization headers, so we pass the token as a query
  // param (?token=). The worker accepts either Bearer header or ?token= on the
  // unload path. Body is sent as a Blob of application/json (a JSON array).
  // eslint-disable-next-line class-methods-use-this
  sendViaBeacon(url, body, token) {
    try {
      if (
        typeof navigator === 'undefined' ||
        typeof navigator.sendBeacon !== 'function'
      ) {
        return;
      }
      const sep = url.includes('?') ? '&' : '?';
      const beaconUrl = `${url}${sep}token=${encodeURIComponent(token)}`;
      const blob =
        typeof Blob !== 'undefined'
          ? new Blob([body], { type: 'application/json' })
          : body;
      navigator.sendBeacon(beaconUrl, blob);
    } catch (e) {
      // swallow
    }
  }

  // Test/teardown helper.
  reset() {
    this.stopTimer();
    this.queue = [];
  }
}

// Singleton — mirrors the AnalyticsHelper singleton pattern so it can be used
// from Options-API methods (like ReplyBox) without a component setup context.
const transport = new UsageAnalyticsTransport();
export default transport;
